/**
 * AI CONTEXT
 * Purpose: selectors/helpers for loading-state atoms (doc/batch/aggregate readiness).
 * Exposes: useDocLoadingState, useBatchLoadingState, useFilteredLoadingState,
 *   useAggregateLoadingState.
 * Used by: components that want explicit initial-load/updating/empty semantics.
 * Importance: standardized loading state interpretation.
 * Refactor: align with current cascading fulfillment hooks to reduce overlap.
 */
/**
 * LOADING STATE HOOK
 *
 * Returns loading/error state and provides utilities for components
 */

import { useAtomValue } from "jotai"
import { useMemo } from "react"

import { LoadingStateAtoms } from "#atoms/loadingStateAtoms"
import { FirebaseQueryConfig } from "#customTypes/composedTypes/firebaseTypes"
import { Uuid } from "#customTypes/globalTypes"
import { stableStringify } from "#utils/textFormatters"

interface UseLoadingStateReturn {
  isInitialLoad: boolean // True until first data received
  isUpdating: boolean // True during updates (for animations)
  hasData: boolean // True once we've received data
  error: Error | null
  isReady: boolean // Convenient: !isInitialLoad && !error
  isEmpty: boolean // Convenient: !isInitialLoad && !hasData && !error
}

/**
 * Hook to get loading state for a document
 */
export function useDocLoadingState(
  loadingAtoms: LoadingStateAtoms,
  id: Uuid | null | undefined
): UseLoadingStateReturn {
  const state = useAtomValue(loadingAtoms.docLoadingStateAtom(id ?? null))

  return {
    ...state,
    isReady: !state.isInitialLoad && !state.error,
    isEmpty: !state.isInitialLoad && !state.hasData && !state.error,
  }
}

/**
 * Hook to get loading state for a batch query
 */
export function useBatchLoadingState(
  loadingAtoms: LoadingStateAtoms,
  batchKey: string
): UseLoadingStateReturn {
  const state = useAtomValue(loadingAtoms.batchLoadingStateAtom(batchKey))

  return {
    ...state,
    isReady: !state.isInitialLoad && !state.error,
    isEmpty: !state.isInitialLoad && !state.hasData && !state.error,
  }
}

/**
 * Hook to get loading state for a `useFilteredDocs` query. Pass the same
 * memoized queryConfig; `isInitialLoad` clears on the first snapshot, even
 * an empty one.
 */
export function useFilteredLoadingState(
  loadingAtoms: LoadingStateAtoms,
  queryConfig: FirebaseQueryConfig
): UseLoadingStateReturn {
  const queryKey = useMemo(
    () => stableStringify(queryConfig) ?? "",
    [queryConfig]
  )
  const state = useAtomValue(loadingAtoms.filteredLoadingStateAtom(queryKey))

  return {
    ...state,
    isReady: !state.isInitialLoad && !state.error,
    isEmpty: !state.isInitialLoad && !state.hasData && !state.error,
  }
}

/**
 * Hook to aggregate loading states (for cascading queries)
 * Returns true only when ALL queries are ready
 */
export function useAggregateLoadingState(states: UseLoadingStateReturn[]): {
  allReady: boolean
  anyLoading: boolean
  anyError: boolean
  errors: (Error | null)[]
} {
  const allReady = states.every((s) => s.isReady)
  const anyLoading = states.some((s) => s.isInitialLoad)
  const anyError = states.some((s) => s.error !== null)
  const errors = states
    .map((s) => s.error)
    .filter((e): e is Error => e !== null)

  return {
    allReady,
    anyLoading,
    anyError,
    errors,
  }
}
